import { useCompanyAnnualRevenue } from '@aibees/elephant/components/company-annual-revenue/use-company-annual-revenue';
import { EntityInfoPanel } from '@aibees/elephant/components/entity-info-panel';
import { useCompanyReadDataContext } from '@aibees/elephant/context';
import { Icon } from '@aibees/shared/components';
import { HighlightableContent } from '@aibees/shared/features';
import { classnames, formatPriceNumber } from '@aibees/shared/utils';
import { Divider, Stack, useTheme } from '@mui/material';
import { memo } from 'react';
import { FinanceInfoCard } from '../finance-info-card';
import * as SX from './sx';
import type { FinancesInfoProps as Props } from './types';
import { useFundingRounds } from './use-funding-rounds';
import { useHasFinancialInfo } from './use-has-financial-info';

export const FinancesInfo = memo(({ className, hideHeader }: Props) => {
  const theme = useTheme();
  const { api } = useCompanyReadDataContext();
  const revenue = useCompanyAnnualRevenue(
    api?.data?.financial?.revenue?.annual?.amount
  );
  const shouldShow = useHasFinancialInfo();
  const fundingRounds = useFundingRounds();

  if (!shouldShow) return null;

  return (
    <EntityInfoPanel
      className={classnames('FinancesInfo', className)}
      sx={SX.root(hideHeader)}
      icon={'finances'}
      title={!hideHeader && 'Finances'}
    >
      {!!api?.data?.financial?.revenue?.annual?.amount && (
        <Stack sx={SX.row}>
          <Stack sx={SX.titleRow}>
            <Icon name="dollar" color={theme.palette.grey[500]} size={20} />

            <HighlightableContent
              content="Annual Revenue"
              variant="body3"
              fontWeight="light"
              color="grey.700"
            />
          </Stack>
          <Stack sx={SX.titleRow}>
            <HighlightableContent
              content={revenue}
              variant="body4"
              fontWeight="700"
              color="grey.700"
            />
          </Stack>
        </Stack>
      )}
      {!!api?.data?.financial?.ipo?.valuation && (
        <>
          <Stack sx={SX.row}>
            <Stack sx={SX.titleRow}>
              <Icon
                name="bar-chart"
                color={theme.palette.grey[500]}
                size={20}
              />

              <HighlightableContent
                content="Market Cap"
                variant="body3"
                fontWeight="light"
                color="grey.700"
              />
            </Stack>
            <Stack sx={SX.titleRow}>
              <HighlightableContent
                content={`$${formatPriceNumber(
                  api?.data?.financial?.ipo?.valuation
                )}`}
                variant="body4"
                fontWeight="700"
                color="grey.700"
              />
            </Stack>
          </Stack>
          <Divider />
        </>
      )}

      {!!api?.data?.financial?.ipo?.link?.label && (
        <>
          <Stack sx={SX.row}>
            <Stack sx={SX.titleRow}>
              <Icon
                name="label-outline"
                color={theme.palette.grey[500]}
                size={20}
              />

              <HighlightableContent
                content="Trading"
                variant="body3"
                fontWeight="light"
                color="grey.700"
              />
            </Stack>
            <Stack sx={SX.titleRow}>
              <HighlightableContent
                content={api?.data?.financial?.ipo?.link?.label}
                variant="body4"
                fontWeight="700"
                color="grey.700"
              />
            </Stack>
          </Stack>
          <Divider />
        </>
      )}

      {api?.data?.financial?.funding?.total_amount != null &&
        api.data.financial.funding.total_amount > 0 && (
          <>
            <Stack sx={SX.row}>
              <Stack sx={SX.titleRow}>
                <Icon name="money" color={theme.palette.grey[500]} size={20} />
                <HighlightableContent
                  content="Total Funding Amount"
                  variant="body3"
                  fontWeight="light"
                  color="grey.700"
                />
              </Stack>
              <Stack sx={SX.titleRow}>
                <HighlightableContent
                  content={`$${formatPriceNumber(
                    api?.data?.financial?.funding?.total_amount ?? 0
                  )}`}
                  variant="body4"
                  fontWeight="bold"
                  color="grey.700"
                />
              </Stack>
            </Stack>
            <Divider />
          </>
        )}

      <Stack p={1.5} gap={1}>
        {fundingRounds.length > 0 ? (
          fundingRounds.map((round, index) => (
            <FinanceInfoCard
              key={index}
              icon="rotate-360"
              title={
                index === fundingRounds.length - 1
                  ? 'Last Funding Round'
                  : 'Funding Round'
              }
              value={round.raised_amount}
              date={round.announced_at}
              type={round.type}
            />
          ))
        ) : (
          <FinanceInfoCard
            icon="rotate-360"
            title="Funding Round"
            value={api?.data?.financial?.funding?.last_amount ?? null}
            date={api?.data?.financial?.funding?.date}
            type={api?.data?.financial?.funding?.type}
          />
        )}
      </Stack>
    </EntityInfoPanel>
  );
});

FinancesInfo.displayName = 'FinancesInfo';
